import { useCallback, useEffect, useRef, useState } from 'react';
import { getVoiceCallOffer, getVoiceMobileConfig } from '../../api/communicationApi';
import {
  clearVoicePush, getVoicePreferences, getVoiceWorker, isVoiceReference, notificationPermission,
  saveVoicePreferences, showCallNotification, synchronizeVoicePush, voiceCapabilities,
  isolateVoicePushOwner, voiceSessionMatches,
} from '../../utils/voiceDevice';

export default function useCallDevice(userId) {
  const [preferences, setPreferences] = useState(() => getVoicePreferences(userId));
  const [permission, setPermission] = useState(notificationPermission);
  const [microphone, setMicrophone] = useState('prompt');
  const [server, setServer] = useState({ enabled: false, reason: 'loading' });
  const [pushState, setPushState] = useState('off');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [offer, setOffer] = useState(null);
  const alive = useRef(true);
  const syncPending = useRef(false);
  const serverRef = useRef(server);
  const capabilities = voiceCapabilities();
  const commit = useCallback((next) => {
    saveVoicePreferences(userId, next);
    setPreferences(next);
  }, [userId]);

  const refresh = useCallback(async () => {
    if (syncPending.current) return;
    if (!voiceSessionMatches(userId)) {
      if (alive.current) { setPreferences((current) => ({ ...current, enabled: false, push: false })); setPushState('off'); }
      return;
    }
    syncPending.current = true;
    try {
      await isolateVoicePushOwner(userId);
      const currentPermission = notificationPermission();
      if (alive.current) setPermission(currentPermission);
      const config = await getVoiceMobileConfig();
      serverRef.current = config;
      if (!alive.current) return;
      setServer(config);
      const latest = getVoicePreferences(userId);
      setPreferences(latest);
      if (latest.push && latest.enabled) {
        setPushState('syncing');
        const result = await synchronizeVoicePush(userId, latest, config);
        if (alive.current) setPushState(result ? 'ready' : 'off');
      }
    } catch (_) {
      if (alive.current) {
        setPushState('error');
        setServer({ enabled: false, reason: 'unavailable' });
      }
    } finally { syncPending.current = false; }
  }, [userId]);

  useEffect(() => {
    alive.current = true;
    // Persist the generated IDs once; subsequent effects must use the same device.
    saveVoicePreferences(userId, getVoicePreferences(userId));
    getVoiceWorker().catch(() => {});
    refresh();
    const interval = setInterval(refresh, 60000);
    const visible = () => { if (document.visibilityState !== 'hidden') refresh(); };
    // The window can flip between hidden and visible repeatedly; the regular
    // refresh above keeps this current, so returning needs only one check a minute.
    let lastReturn = 0;
    const returned = () => {
      if (Date.now() - lastReturn < 60000) return;
      lastReturn = Date.now();
      visible();
    };
    const changed = () => setPreferences(getVoicePreferences(userId));
    const storage = (event) => {
      if (event.key === 'token' && !voiceSessionMatches(userId)) {
        setPreferences((current) => ({ ...current, enabled: false, push: false }));
        setPushState('off');
      } else if (event.key === 'voice-reception:' + userId) { changed(); visible(); }
    };
    window.addEventListener('voice-preferences-changed', changed);
    window.addEventListener('storage', storage);
    window.addEventListener('online', visible);
    document.addEventListener('visibilitychange', returned);
    let micPermission;
    navigator.permissions?.query({ name: 'microphone' }).then((result) => {
      if (!alive.current) return;
      micPermission = result;
      const update = () => { if (alive.current) setMicrophone(result.state); };
      result.onchange = update;
      update();
    }).catch(() => {});
    return () => {
      alive.current = false;
      clearInterval(interval);
      window.removeEventListener('voice-preferences-changed', changed);
      window.removeEventListener('storage', storage);
      window.removeEventListener('online', visible);
      document.removeEventListener('visibilitychange', returned);
      if (micPermission) micPermission.onchange = null;
    };
  }, [userId, refresh]);

  useEffect(() => {
    const reference = new URLSearchParams(window.location.search).get('voiceOffer');
    if (!isVoiceReference(reference)) return undefined;
    let cancelled = false;
    let timer;
    let remaining = 12;
    const inspect = async () => {
      try {
        const result = await getVoiceCallOffer(reference);
        if (cancelled) return;
        const messages = {
          ringing: 'Der Anruf klingelt. Die Verbindung zu diesem Gerät wird geprüft.',
          waiting: 'Der Anruf wartet. Dieses Gerät wird mit der Firmenleitung verbunden.',
          answered: 'Dieser Anruf wurde bereits auf einem Gerät angenommen.',
          ended: 'Dieser Anruf ist bereits beendet.',
          missed: 'Der Anruf ist nicht mehr verfügbar. Bitte die Anrufhistorie prüfen.',
        };
        setOffer({ message: messages[result.state] || 'Anrufstatus konnte nicht ermittelt werden.' });
        if (['ringing', 'waiting'].includes(result.state) && --remaining > 0) timer = setTimeout(inspect, 3000);
      } catch (failure) {
        if (cancelled) return;
        const denied = [401, 403, 404].includes(failure.response?.status);
        setOffer({ message: denied ? 'Der Anruf ist für diese Anmeldung nicht verfügbar.'
          : 'Anrufstatus konnte nicht geladen werden. Bitte die Verbindung prüfen.', action: !denied });
      }
    };
    inspect();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [userId]);

  const run = async (action) => {
    setBusy(true); setError('');
    try { await action(); }
    catch (failure) { if (alive.current) setError(failure.message || 'Einrichtung konnte nicht abgeschlossen werden.'); }
    finally { if (alive.current) setBusy(false); }
  };
  const onEnabled = (enabled) => run(async () => {
    const current = getVoicePreferences(userId);
    if (!enabled) {
      commit({ ...current, enabled: false, push: false, epoch: window.crypto.randomUUID() });
      await clearVoicePush(userId, current);
      if (alive.current) setPushState('off');
    } else commit({ ...current, enabled: true });
  });
  const onMicrophone = () => run(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      if (alive.current) setMicrophone('granted');
    } catch (_) {
      setMicrophone('denied');
      throw new Error('Mikrofon bitte in den Browser- oder Geräteeinstellungen erlauben.');
    }
  });
  const requestNotifications = async () => {
    if (voiceCapabilities().needsHomeScreen) throw new Error('Bitte das ERP zuerst über das Symbol auf dem Home-Bildschirm öffnen.');
    if (!('Notification' in window)) throw new Error('Dieser Browser unterstützt keine Systemhinweise.');
    const next = await Notification.requestPermission();
    if (alive.current) setPermission(next);
    if (next !== 'granted') throw new Error('Benachrichtigungen bitte in den Geräteeinstellungen erlauben.');
    return next;
  };
  const onNotifications = () => run(requestNotifications);
  const onTestNotification = () => run(() => showCallNotification(null, true));
  const onPush = (enabled) => run(async () => {
    const current = getVoicePreferences(userId);
    if (!enabled) {
      commit({ ...current, push: false, epoch: window.crypto.randomUUID() });
      await clearVoicePush(userId, current);
      if (alive.current) setPushState('off');
      return;
    }
    // This call starts inside the user's tap, before awaiting service-worker setup.
    await requestNotifications();
    const next = { ...current, push: true, epoch: window.crypto.randomUUID() };
    commit(next);
    setPushState('syncing');
    try {
      const result = await synchronizeVoicePush(userId, next, serverRef.current);
      if (!result) throw new Error('Hintergrundhinweise sind serverseitig noch nicht eingerichtet.');
      if (alive.current) setPushState('ready');
    } catch (_) {
      commit({ ...next, push: false });
      await clearVoicePush(userId, next);
      setPushState('error');
      throw new Error('Hintergrundhinweise konnten nicht eingerichtet werden. Anmeldung und Servereinrichtung bitte prüfen.');
    }
  });
  return { preferences, permission, microphone, capabilities, server, pushState, busy, error, offer,
    onEnabled, onMicrophone, onNotifications, onPush, onTestNotification };
}
